import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { LoadingButton } from '@mui/lab';
import {
  Alert,
  Box,
  Button,
  Container,
  Grid,
  MenuItem,
  Select,
  Stack,
  SxProps,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import ArrowBack from '@mui/icons-material/ArrowBack';
import InfoIcon from '@mui/icons-material/Info';

import {
  IQuiz,
  // getQuizState,
  resetQuiz,
} from '../../../store/QuizSlice';
import {
  getDiagnosticBySubTopicId,
  getDiagnosticState,
  resetDiagnostic,
} from '../../../store/DiagnosticSlice';
import {
  getSubTopicById,
  getSubTopicState,
} from '../../../store/SubTopicSlice';
import {
  createNewSubmissionByQuizId,
  getQuizDataForAboutPage,
  getSubmissionState,
} from '../../../store/SubmissionSlice';
import { getAuthState } from '../../../store/AuthSlice';
import { TP_COLORS } from '../../../constants';
import { useAppSelector } from '../../../hooks';
import {
  SafeAreaGaps,
  checkUserPermission,
  getProductValueForQuizShowedFromLocalStorage,
  getUrlConvertedString,
  setProductValueForQuizShowedToLocalStorage,
} from '../../../helper';
import ProductValueForQuiz from '../ProductValue/ProductValueForQuiz';

type TPaperOption = {
  label: string;
  value: string;
  item: {
    paper: string;
    quiz: IQuiz;
  };
};

type TCustomTdProps = {
  width?: string;
  color?: string;
  children: React.ReactElement | string;
};

const CustomTd = ({
  color = '#01736D',
  width = undefined,
  children,
}: TCustomTdProps) => (
  <Typography
    component="td"
    sx={{
      width,
      color,
      fontSize: { xs: '12px', sm: 'unset' },
      fontWeight: 600,
    }}
  >
    {children}
  </Typography>
);

const CustomAlert = ({ buttonData, alertText }) => {
  return (
    <Alert
      severity="info"
      icon={<InfoIcon sx={{ color: '#01736D' }} />}
      sx={{
        backgroundColor: TP_COLORS.white,
        color: '#01736D',
        borderRadius: '25px',
        marginBottom: '15px',
      }}
      action={
        <Button
          size="small"
          color="primary"
          variant="contained"
          onClick={buttonData.onClick}
          sx={{
            borderRadius: '30px',
            backgroundColor: '#01736D',
            color: TP_COLORS.white,
          }}
        >
          {buttonData.text}
        </Button>
      }
    >
      {alertText}
    </Alert>
  );
};

const containerStyles: SxProps = {
  background: {
    xs: `url('${process.env.PUBLIC_URL}/images/quiz/about-quiz-hand.png') bottom center/contain no-repeat, linear-gradient(180deg, #01736D 0%, #001615 100%)`,
    md: 'linear-gradient(180deg, #01736D 0%, #001615 100%)',
  },
  minHeight: { xs: 'calc(100vh - 56px)', sm: 'calc(100vh - 64px)' },
};

const AboutQuiz: React.FC = () => {
  const params = useParams();
  const location = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useTheme();
  const belowMd = useMediaQuery(theme.breakpoints.down('md'));

  // Store
  const subTopicState = useAppSelector(getSubTopicState);
  const submissionState = useAppSelector(getSubmissionState);
  // const quizState = useAppSelector(getQuizState);
  const authState = useAppSelector(getAuthState);
  const diagnosticState = useAppSelector(getDiagnosticState);

  // Permissions
  const createSubmissionPermission = checkUserPermission(authState.user, {
    submission: ['create'],
  });

  // State
  const [
    selectedPaperData,
    setSelectedPaperData,
  ] = useState<TPaperOption | null>(null);
  // const [redirect, setRedirect] = useState(false);
  const [isProductValueForQuizOpen, setProductValueForQuiz] = useState(false);

  let urlConvertedCurriculum = '';
  let urlConvertedSubject = '';
  let urlConvertedTopic = '';
  let urlConvertedSubTopic = '';

  const subTopicId = params.subtopicId;
  const subTopicData = subTopicState.subTopic;
  const quizData = submissionState.quizDataForAboutPage;
  const topicData =
    subTopicData && subTopicData.topic ? subTopicData.topic : undefined;

  const quizCount =
    subTopicData && subTopicData.quizCount ? subTopicData.quizCount : undefined;

  const isDiagnostic =
    (topicData && topicData.name === 'Diagnostic Challenge') || false;

  const diagnosticData = diagnosticState.diagnostic;

  const queryStringParams = new URLSearchParams(
    `?${location.hash.substring(1)}`
  );

  const urlPaper = queryStringParams.get('paper') || '';
  const paperOptions: Array<TPaperOption> = [];

  if (quizData && quizData.list && quizData.list.length) {
    quizData.list.forEach((item) => {
      paperOptions.push({
        item,
        label: item.paper,
        value: item.quiz && item.quiz._id ? item.quiz._id : '',
      });
    });
  }

  if (topicData) {
    urlConvertedCurriculum = getUrlConvertedString(topicData.curriculum);
    urlConvertedSubject = getUrlConvertedString(topicData.subject);
    urlConvertedTopic = getUrlConvertedString(topicData.name);
  }

  if (subTopicData) {
    urlConvertedSubTopic = getUrlConvertedString(subTopicData.name);
  }

  const showedCount = 1;
  const productValueForQuizShowed = getProductValueForQuizShowedFromLocalStorage();

  useEffect(() => {
    dispatch(resetQuiz());
    if (
      !productValueForQuizShowed ||
      parseFloat(productValueForQuizShowed) < showedCount
    ) {
      setProductValueForQuiz(true);
    }

    return () => {
      dispatch(resetQuiz());
      dispatch(resetDiagnostic());
    };
  }, []);

  useEffect(() => {
    if (subTopicId) {
      dispatch(getSubTopicById(subTopicId));
      dispatch(getQuizDataForAboutPage({ subTopicId }));
    }
  }, [subTopicId]);

  useEffect(() => {
    if (quizData && quizData.list && quizData.list.length) {
      let selectedItem = quizData.list[0];

      if (urlPaper) {
        const matchItem = quizData.list.find((item) => item.paper === urlPaper);

        if (matchItem) {
          selectedItem = matchItem;
        }
      }

      setSelectedPaperData({
        label: selectedItem.paper,
        value:
          selectedItem.quiz && selectedItem.quiz._id
            ? selectedItem.quiz._id
            : '',
        item: selectedItem,
      });
    }
  }, [quizData]);

  useEffect(() => {
    if (
      subTopicId &&
      isDiagnostic &&
      diagnosticState.status !== 'loading' &&
      authState.user
    ) {
      dispatch(
        getDiagnosticBySubTopicId({ subTopicId, userId: authState.user._id })
      );
    }
  }, [isDiagnostic]);

  const handlePaperChange = (option: TPaperOption) => {
    setSelectedPaperData(option);
  };

  const startQuiz = () => {
    if (selectedPaperData && selectedPaperData.value && subTopicId) {
      dispatch(
        createNewSubmissionByQuizId({
          subTopicId,
          quizId: selectedPaperData.value,
        })
      );
    }
  };

  const viewQuizHistory = () => {
    if (selectedPaperData) {
      navigate(
        `${process.env.PUBLIC_URL}/dashboard/${urlConvertedCurriculum}/${urlConvertedSubject}/${urlConvertedTopic}/${urlConvertedSubTopic}/${subTopicId}/quiz-history/${selectedPaperData.value}`
      );
    }
  };

  return (
    <Box sx={containerStyles}>
      <Container maxWidth="lg">
        <div style={{ padding: '30px 0 0 0' }}>
          <Button
            size="large"
            variant="text"
            startIcon={<ArrowBack />}
            onClick={() => navigate(-1)}
            style={{
              textDecoration: 'underline',
              color: TP_COLORS.white,
              borderRadius: '30px',
            }}
          >
            Back
          </Button>
        </div>
        {diagnosticData && diagnosticData._id ? (
          <Stack alignItems="center">
            <CustomAlert
              alertText="Check your previous assessment report"
              buttonData={{
                text: 'Click here',
                onClick: () => {
                  navigate(
                    `${process.env.PUBLIC_URL}/dashboard/${urlConvertedCurriculum}/${urlConvertedSubject}/${urlConvertedTopic}/${urlConvertedSubTopic}/${subTopicId}/diagnostic-quiz/${diagnosticData._id}`
                  );
                },
              }}
            />
          </Stack>
        ) : null}
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={12} md={6}>
            <div>
              <Typography variant="h3" color={TP_COLORS.white}>
                About This Quiz
              </Typography>
            </div>
            <div style={{ padding: '30px 0' }}>
              <Typography
                color={TP_COLORS.white}
                fontSize="20px"
                textAlign="justify"
              >
                Before you begin the quiz, make sure you have a strong WIFI
                connection, a pen and paper to do any extra calculations and a
                water bottle or drink to get you thinking.
              </Typography>
            </div>
            <Box sx={{ marginBottom: { xs: '15px', md: '30px' } }}>
              <table className="about-quiz-info-table">
                <thead>
                  <tr>
                    <CustomTd>No.of Questions</CustomTd>
                    {/* <CustomTd>Time</CustomTd> */}
                    <CustomTd>Difficulty Level</CustomTd>
                    <CustomTd width="160px">Paper</CustomTd>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <CustomTd color="#006C9C">
                      {selectedPaperData &&
                      selectedPaperData.item &&
                      selectedPaperData.item.quiz &&
                      selectedPaperData.item.quiz.noOfQuestions >= 0
                        ? `${selectedPaperData.item.quiz.noOfQuestions} Questions`
                        : '-'}
                    </CustomTd>
                    {/* <CustomTd>7 min 22 sec</CustomTd> */}
                    <CustomTd color="#006C9C">
                      {selectedPaperData &&
                      selectedPaperData.item &&
                      selectedPaperData.item.quiz &&
                      selectedPaperData.item.quiz.difficulty
                        ? selectedPaperData.item.quiz.difficulty
                        : '-'}
                    </CustomTd>
                    <CustomTd color="#006C9C">
                      {paperOptions.length ? (
                        <Select
                          fullWidth
                          size="small"
                          value={selectedPaperData?.label || ''}
                          sx={{
                            color: '#006C9C',
                            fontSize: 'inherit',
                            fontWeight: 600,
                            backgroundColor: TP_COLORS.white,
                            borderRadius: '12px',
                            maxWidth: '150px',
                          }}
                        >
                          {paperOptions.map((item, index) => (
                            <MenuItem
                              key={index}
                              value={item.label}
                              onClick={() => handlePaperChange(item)}
                            >
                              {item.label}
                            </MenuItem>
                          ))}
                        </Select>
                      ) : (
                        '-'
                      )}
                    </CustomTd>
                  </tr>
                </tbody>
              </table>
            </Box>
            <Box
              sx={
                belowMd
                  ? {
                      position: 'fixed',
                      bottom: `15px`,
                      left: `${SafeAreaGaps.left}`,
                      width: '100%',
                      padding: '0 15px',
                    }
                  : undefined
              }
            >
              <Grid container spacing={2}>
                {createSubmissionPermission ? (
                  <Grid item xs={6}>
                    <LoadingButton
                      fullWidth
                      variant="contained"
                      size="large"
                      color="inherit"
                      loadingPosition="start"
                      onClick={startQuiz}
                      disabled={!selectedPaperData || !quizCount}
                      loading={submissionState.status === 'loading'}
                      style={{
                        borderRadius: '30px',
                        color: 'rgba(0, 108, 156, 1)',
                        boxShadow: 'rgb(1, 115, 109) 1px 3px 0px',
                        fontWeight: 600,
                        backgroundColor: TP_COLORS.white,
                        paddingTop: '10px',
                        paddingBottom: '10px',
                      }}
                    >
                      {quizCount ? 'Start a Quiz' : 'Quiz Upcoming'}
                    </LoadingButton>
                  </Grid>
                ) : null}
                <Grid item xs={6}>
                  <Button
                    fullWidth
                    variant="text"
                    size="large"
                    color="inherit"
                    disabled={!selectedPaperData}
                    onClick={viewQuizHistory}
                    style={{
                      color: TP_COLORS.white,
                      textDecoration: 'underline',
                      fontWeight: 600,
                      borderRadius: '30px',
                      maxWidth: '120px',
                    }}
                  >
                    View History
                  </Button>
                </Grid>
              </Grid>
            </Box>
          </Grid>
          <Grid item xs={12} sm={12} md={6}>
            <Box sx={{ display: { xs: 'none', md: 'flex' } }}>
              <img
                src={`${process.env.PUBLIC_URL}/images/quiz/about-quiz-hand.png`}
                alt="about-quiz"
                style={{ width: '100%' }}
              />
            </Box>
            <Box sx={{ height: { xs: '300px', md: 0 } }} />
          </Grid>
        </Grid>
        {/* MODALS */}
        <ProductValueForQuiz
          open={isProductValueForQuizOpen}
          onClose={() => {
            setProductValueForQuiz(false);
            setProductValueForQuizShowedToLocalStorage(showedCount.toString());
          }}
        />
      </Container>
    </Box>
  );
};

export default AboutQuiz;
